import { isPlatformBrowser } from '@angular/common';
import { HttpInterceptorFn, HttpResponse } from '@angular/common/http';
import { inject, PLATFORM_ID } from '@angular/core';
import { Store } from '@ngrx/store';
import { tap } from 'rxjs';
import { SelectRegionalCountry } from 'projects/shop/src/app/layout/navbar/components/navbar-header/partials/nav-regional/store/regional-setting.selectors';
import { countriesService } from 'projects/shop/src/app/modules/home/services/countries.service';
import { VISITOR_IP } from '../services/visitor-ip';
import { SSR_RESPONSE } from '../services/ssr-response';

function toSnakeCase(str: string | null | undefined): string {
  if (!str || typeof str !== 'string') return '';
  return str.toLowerCase().trim().replace(/\s+/g, '_');
}

/**
 * The visitor's own country choice (regional settings), as an ISO code, or null when there is
 * none yet. It used to default to Egypt here, so a first visit from abroad, and every SSR render
 * (including Googlebot), was treated as Egypt. With no header the API now resolves the country
 * from the visitor's IP instead.
 */
function getSelectedCountryCode(codes: Record<string, string>): string | null {
  const store = inject(Store);
  const country = store.selectSignal(SelectRegionalCountry)();
  if (!country) return null;
  return codes[toSnakeCase(country)] ?? null;
}

export const UserCountryInterceptor: HttpInterceptorFn = (request, next) => {
  const countriesCodes = inject(countriesService);
  const isBrowser = isPlatformBrowser(inject(PLATFORM_ID));
  const visitorIp = inject(VISITOR_IP, { optional: true });
  const ssrResponse = inject(SSR_RESPONSE, { optional: true });

  let headers = request.headers;

  // The first suggestion (language/currency/country) must come from the IP, not from a stale choice
  if (!request.url.includes('suggest-language-currency')) {
    const selected = getSelectedCountryCode(countriesCodes.countryCodes);
    if (selected) headers = headers.set('X-User-Country', selected);
  }

  // During SSR the server calls the API: pass the visitor's IP along for the country lookup.
  // Without a usable IP keep the old behaviour (Egypt) rather than let the API look up the
  // SSR server's own IP.
  if (!isBrowser) {
    if (visitorIp) {
      headers = headers.set('X-Visitor-IP', visitorIp);
    } else if (!headers.has('X-User-Country')) {
      headers = headers.set('X-User-Country', 'EG');
    }
  }

  const outgoing = headers === request.headers ? request : request.clone({ headers });

  if (isBrowser || !ssrResponse) return next(outgoing);

  // Remember which country the API resolved, so the SSR cache can be split per country
  return next(outgoing).pipe(
    tap(event => {
      if (event instanceof HttpResponse && !ssrResponse.country) {
        const resolved = event.headers.get('X-Resolved-Country');
        if (resolved && /^[A-Z]{2}$/.test(resolved)) ssrResponse.country = resolved;
      }
    })
  );
};
